import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ListingAuditDrawer from './ListingAuditDrawer';
import { runAudit } from '../../utils/audit';

const AuditDrawerContext = createContext(null);

export function useAuditDrawer() {
  const ctx = useContext(AuditDrawerContext);
  if (!ctx) throw new Error('useAuditDrawer must be used within AuditDrawerProvider');
  return ctx;
}

/**
 * Props:
 * - onQuickFix(key, product)
 * - onLoadProduct(product)
 * - onRerunAuditForProduct?(product): Promise<object|null>
 * - onUpdateAuditOverrides?(productId, overrides): Promise<void>
 * - getProductById?(id): object | null
 * - onOpenCoachPanel?(productId): void   // navigates to the Product Coaching panel for this product
 */
export default function AuditDrawerProvider({
  children,
  onQuickFix,
  onLoadProduct,
  onRerunAuditForProduct,
  onUpdateAuditOverrides,
  getProductById,
  onOpenCoachPanel, // <-- keep wiring for the bottom button
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [activeAudit, setActiveAudit] = useState(null);

  // Open with a product + (optionally) a precomputed audit for fast paint.
  const openAudit = useCallback(
    (product, auditResult) => {
      setActiveProduct(product || null);
      setActiveAudit(auditResult || (product ? runAudit(product, product.auditOverrides || {}) : null));
      setIsOpen(true);

      if (onLoadProduct && product) onLoadProduct(product);
    },
    [onLoadProduct]
  );

  const closeAudit = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Pull the freshest product and update local drawer state.
  const syncActiveProduct = useCallback(async (productId) => {
    const id = productId || activeProduct?.id;
    if (!id || !getProductById) return;

    try {
      const latest = getProductById(id);
      if (latest) {
        setActiveProduct(latest);

        // If the host provides a re-run, prefer it; otherwise recompute locally.
        if (onRerunAuditForProduct) {
          try {
            const rerun = await onRerunAuditForProduct(latest);
            setActiveAudit(rerun || runAudit(latest, latest.auditOverrides || {}));
          } catch {
            setActiveAudit(runAudit(latest, latest.auditOverrides || {}));
          }
        } else {
          setActiveAudit(runAudit(latest, latest.auditOverrides || {}));
        }
      }
    } catch (e) {
      console.error('syncActiveProduct error:', e);
    }
  }, [activeProduct?.id, getProductById, onRerunAuditForProduct]);

  // Basic in-provider re-run (kept for completeness).
  const handleRerun = useCallback(async () => {
    if (!activeProduct) return;
    if (onRerunAuditForProduct) {
      try {
        const newAuditResult = await onRerunAuditForProduct(activeProduct);
        if (newAuditResult) {
          setActiveAudit(newAuditResult);
          return;
        }
      } catch (error) {
        console.error('Error re-running audit from provider:', error);
      }
    }
    // fallback: local recompute
    setActiveAudit(runAudit(activeProduct, activeProduct.auditOverrides || {}));
  }, [activeProduct, onRerunAuditForProduct]);

  const value = useMemo(
    () => ({
      openAudit,
      closeAudit,
    }),
    [openAudit, closeAudit]
  );

  return (
    <AuditDrawerContext.Provider value={value}>
      {children}
      <ListingAuditDrawer
        isOpen={isOpen}
        onClose={closeAudit}
        product={activeProduct}
        audit={activeAudit}
        onQuickFix={onQuickFix}
        onRerunAudit={handleRerun}
        onUpdateAuditOverrides={onUpdateAuditOverrides}
        onSyncActiveProduct={syncActiveProduct}
        onOpenCoachPanel={onOpenCoachPanel}
      />
    </AuditDrawerContext.Provider>
  );
}
